// 스킬 번호와 입력 비트를 분리한다. 모든 표시 표면은 이 주소를 공유한다.
import { assetUrl } from '../pwa/assetMap';
import { heroSkills } from '../shared/skills/registry';
import { SKILL_BTN_ALL, type PlayableHeroId } from '../shared/types';

export function skillIdOfBtn(bit: number): number {
  const i = SKILL_BTN_ALL.indexOf(bit as typeof SKILL_BTN_ALL[number]);
  return i < 0 ? 0 : i + 1;
}

/**
 * 전직 스킬(카엘 9·10)의 HUD 그림은 임시 — Codex 원화 대기. 원화가 오기 전까지 비슷한 기존 카엘 그림을 빌려 쓴다
 * (9 = 폭주·검기 → 이동 회전 8번, 10 = 흡혈 참격·천검 낙하 → 직선 광역 7번). 원화가 들어오면 이 표만 지우고 kael-9/10.webp를 넣는다.
 */
const TEMP_JOB_ART: Readonly<Partial<Record<PlayableHeroId, readonly [Readonly<Record<number, number>>, Readonly<Record<number, number>>]>>> = {
  kael: [{ 9: 8, 10: 7 }, { 9: 8, 10: 7 }],
};

/**
 * 전직 스킬 원화(Codex CLI image_gen, 2026-10-27 skill-sheet-2, public/assets/ui/skills/icons-manifest.json 해시) — 갈래 a/b × 전직 I(30)·II(40).
 * 라이라 12 = job30, 13 = job40(윈드 스나이퍼 a: 차지샷·관통 폭풍 / 트랩 레인저 b: 지뢰밭·덫 연쇄). 표에 있으면 임시 그림보다 먼저 쓴다.
 */
const JOB_ART: Readonly<Partial<Record<PlayableHeroId, Readonly<Record<number, string>>>>> = {
  lyra: { 12: 'job30', 13: 'job40' },
};

/** 실제 타임라인에 없는 미래 번호에는 그림 주소도 만들지 않는다. job = 전직 갈래(전직 스킬 원화·임시 그림 고르기) */
export function skillArtUrl(hero: PlayableHeroId, skill: number, job: 0 | 1 = 0): string | null {
  if (!Number.isInteger(skill) || !heroSkills(hero, job).some((s) => s.id === skill)) return null;
  const jobArt = JOB_ART[hero]?.[skill];
  if (jobArt) return assetUrl('ui/skills/' + hero + '_' + jobArt + '_' + (job === 1 ? 'b' : 'a') + '.webp');
  const file = TEMP_JOB_ART[hero]?.[job === 1 ? 1 : 0][skill] ?? skill;
  return assetUrl('ui/skill-hud-v2/' + hero + '-' + file + '.webp');
}

export interface SkillArt {
  readonly root: HTMLSpanElement;
  set(hero: PlayableHeroId, skill: number, fallback: string, job?: 0 | 1): void;
}

/** SVG는 네트워크 실패 후에만 켠다. 영웅 전환 전 요청의 늦은 load/error는 무시한다. */
export function mountSkillArt(parent: HTMLElement, hero: PlayableHeroId, skill: number, fallback: string, job: 0 | 1 = 0): SkillArt {
  const root = document.createElement('span');
  root.className = 'dh-skill-art';
  root.style.cssText = 'position:absolute;inset:0;display:block;pointer-events:none;overflow:hidden;border-radius:inherit;';
  root.setAttribute('aria-hidden', 'true');
  parent.appendChild(root);
  let key = '';
  let generation = 0;
  const art: SkillArt = { root, set(h, id, fb, j = 0) {
    const next = h + ':' + id + ':' + j;
    if (key === next) return;
    key = next;
    const ticket = ++generation;
    root.textContent = '';
    root.dataset.hero = h;
    root.dataset.skill = String(id);
    root.dataset.state = 'loading';
    const url = skillArtUrl(h, id, j);
    if (!url) { root.dataset.state = 'absent'; return; }
    const im = document.createElement('img');
    im.alt = '';
    im.draggable = false;
    im.decoding = 'async';
    // contain: 칼끝·화살 끝을 원형 마스크로 자르지 않는다.
    im.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;';
    im.addEventListener('load', () => { if (ticket === generation) root.dataset.state = 'loaded'; });
    im.addEventListener('error', () => {
      if (ticket !== generation) return;
      root.dataset.state = 'fallback';
      root.innerHTML = '<span style="position:absolute;inset:12%;display:grid;place-items:center">' + fb + '</span>';
    });
    root.appendChild(im);
    im.src = url;
  } };
  art.set(hero, skill, fallback, job);
  return art;
}
